import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useToast } from './Toast';
import { generateVacancyPDFFromSections } from '../../utils/vacancyPdfExport';
import { generateVacancyDocx } from '../../utils/vacancyDocxExport';
import ShareMenu from '../document/ShareMenu';

/* ── DownloadMenu, dropdown with PDF + Word options ── */
function DownloadMenu({ onPDF, onDocx }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="download-menu" ref={ref}>
      <button
        type="button"
        className="completion-screen__btn completion-screen__btn--primary"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="material-icons">download</span>
        Download
        <span className="material-icons" style={{ fontSize: 16, marginLeft: 2 }}>
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {open && (
        <div className="download-menu__dropdown">
          <button type="button" className="download-menu__item" onClick={() => { onPDF(); setOpen(false); }}>
            <span className="material-icons" style={{ color: '#e53e3e' }}>picture_as_pdf</span>
            <span>PDF</span>
          </button>
          <button type="button" className="download-menu__item" onClick={() => { onDocx(); setOpen(false); }}>
            <span className="material-icons" style={{ color: '#2b6cb0' }}>description</span>
            <span>Word (.docx)</span>
          </button>
        </div>
      )}
    </div>
  );
}

const SECTION_ORDER = [
  'job_title', 'about_company', 'job_description',
  'requirements', 'benefits', 'conditions',
];

const SECTION_LABELS = {
  job_title: 'Vacaturetitel',
  about_company: 'Over het bedrijf',
  job_description: 'Functieomschrijving',
  requirements: 'Functie-eisen',
  benefits: 'Wat wij bieden',
  conditions: 'Arbeidsvoorwaarden',
};

export default function CompletionScreen({ onDismiss, onEditSection }) {
  const { state } = useWorkspace();
  const showToast = useToast();
  const { sections, title } = state.document;
  const [activeSection, setActiveSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const editInputRef = useRef(null);

  const buildText = useCallback(() => {
    const parts = [];
    if (title) { parts.push(title); parts.push(''); }
    SECTION_ORDER.forEach((key) => {
      const section = sections[key];
      if (section?.content) {
        parts.push(SECTION_LABELS[key]);
        parts.push(section.content);
        parts.push('');
      }
    });
    return parts.join('\n').trim();
  }, [sections, title]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(buildText()).then(
      () => showToast('Vacaturetekst gekopieerd', 'success'),
      () => showToast('Kopiëren mislukt', 'error'),
    );
  }, [buildText, showToast]);

  const handlePDF = useCallback(() => {
    generateVacancyPDFFromSections({ sections, title });
    showToast('PDF gedownload', 'success');
  }, [sections, title, showToast]);

  const handleDocx = useCallback(async () => {
    const ok = await generateVacancyDocx({ sections, title });
    showToast(ok ? 'Word-document gedownload' : 'Geen tekst om te downloaden', ok ? 'success' : 'info');
  }, [sections, title, showToast]);

  const handleStartEdit = useCallback((key) => {
    setEditingSection(key);
    setEditPrompt('');
    setTimeout(() => editInputRef.current?.focus(), 50);
  }, []);

  const handleSubmitEdit = useCallback(async (key) => {
    if (!editPrompt.trim() || !onEditSection) return;
    setEditLoading(true);
    try {
      await onEditSection(key, editPrompt.trim());
      showToast(`${SECTION_LABELS[key]} wordt aangepast...`, 'info');
      setEditingSection(null);
      setEditPrompt('');
    } catch {
      showToast('Aanpassing mislukt', 'error');
    } finally {
      setEditLoading(false);
    }
  }, [editPrompt, onEditSection, showToast]);

  const handleCopySection = useCallback((key) => {
    const label = key === 'job_title' ? '' : `${SECTION_LABELS[key]}\n`;
    const text = `${label}${sections[key].content}`;
    navigator.clipboard.writeText(text).then(
      () => showToast(`${SECTION_LABELS[key]} gekopieerd`, 'success'),
      () => showToast('Kopiëren mislukt', 'error'),
    );
  }, [sections, showToast]);

  const filledSections = SECTION_ORDER.filter(k => sections[k]?.content?.trim());

  return (
    <div className="completion-screen">
      <div className="completion-screen__backdrop" onClick={onDismiss} />
      <div className="completion-screen__content">

        {/* Header, compact row layout */}
        <div className="completion-screen__header">
          <span className="completion-screen__check material-icons">check_circle</span>
          <div className="completion-screen__header-text">
            <h1 className="completion-screen__title">
              {title || 'Vacaturetekst compleet'}
            </h1>
            <p className="completion-screen__subtitle">Klaar! Bekijk, download of deel.</p>
          </div>
          <button
            type="button"
            className="completion-screen__close"
            onClick={onDismiss}
            aria-label="Sluiten"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* Vacancy text */}
        <div className="completion-screen__document">
          {filledSections.map((key) => (
            <div
              key={key}
              className={`completion-section ${activeSection === key ? 'completion-section--active' : ''}`}
              onClick={() => setActiveSection(activeSection === key ? null : key)}
            >
              <div className="completion-section__head">
                {key !== 'job_title' && (
                  <h3 className="completion-section__label">
                    {SECTION_LABELS[key]}
                  </h3>
                )}
                <div className="completion-section__head-actions">
                  {onEditSection && (
                    <button
                      type="button"
                      className="completion-section__copy"
                      onClick={(e) => { e.stopPropagation(); handleStartEdit(key); }}
                      title={`Bewerk ${SECTION_LABELS[key]}`}
                    >
                      <span className="material-icons">edit</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="completion-section__copy"
                    onClick={(e) => { e.stopPropagation(); handleCopySection(key); }}
                    title={`Kopieer ${SECTION_LABELS[key]}`}
                  >
                    <span className="material-icons">content_copy</span>
                  </button>
                </div>
              </div>
              {editingSection === key && (
                <div className="completion-section__edit" onClick={(e) => e.stopPropagation()}>
                  <input
                    ref={editInputRef}
                    className="completion-section__edit-input"
                    type="text"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitEdit(key); if (e.key === 'Escape') setEditingSection(null); }}
                    placeholder={`Bijv. "maak korter" of "voeg teamgrootte toe"`}
                    disabled={editLoading}
                  />
                  <button
                    type="button"
                    className="completion-section__edit-send"
                    onClick={() => handleSubmitEdit(key)}
                    disabled={editLoading || !editPrompt.trim()}
                  >
                    <span className="material-icons">{editLoading ? 'hourglass_empty' : 'send'}</span>
                  </button>
                </div>
              )}
              <div className={`completion-section__content ${key === 'job_title' ? 'completion-section__content--title' : ''}`}>
                {sections[key].content.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <br key={i} />;
                  if (/^[•\-\u2022\u2013*]\s/.test(trimmed)) {
                    return (
                      <div key={i} className="completion-section__bullet">
                        <span className="completion-section__dot" />
                        {trimmed.replace(/^[•\-\u2022\u2013*]\s*/, '')}
                      </div>
                    );
                  }
                  return <p key={i} className="completion-section__para">{trimmed}</p>;
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="completion-screen__actions">
          <button type="button" className="completion-screen__btn completion-screen__btn--primary" onClick={handleCopy}>
            <span className="material-icons">content_copy</span>
            Kopieer
          </button>
          <DownloadMenu onPDF={handlePDF} onDocx={handleDocx} />
          <ShareMenu getText={buildText} getTitle={() => title} showToast={showToast} />
          <button type="button" className="completion-screen__btn completion-screen__btn--secondary" onClick={onDismiss}>
            <span className="material-icons">edit</span>
            Bewerken
          </button>
        </div>
      </div>
    </div>
  );
}
